import { Component, OnInit, ViewChild } from '@angular/core';
import { AssessmentSearchCtxService } from '../service/assessment-search-ctx.service';
import { ReportService } from '../../report/service/report.service';
import { Tag } from '../../domain/nztcs/tag';
import { EDIT_STATUS_VALUES } from '../../domain/nztcs/edit-status';
import { TagService } from '../../tag/service/tag.service';
import { AuthenticationService } from '../../authentication/service/authentication.service';
import { User } from '../../authentication/service/user';
import { UmbrellaCategories } from '../../domain/nztcs/umbrella-categories';
import { TaxonomicStatus } from '../../domain/nztcs/taxonomic-status';
import { AssessmentExportService } from '../service/assessment-export.service';
import { Router } from '@angular/router';
import { NztcsSpeciesService } from '../../nztcs-species/service/nztcs-species.service';
import { Observable } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { Environment } from '../../domain/nztcs/environment';
import { AssessmentService } from '../service/assessment.service';
import { NztcsSpeciesNameComparisonService } from '../../nztcs-species/service/nztcs-species-name-comparison.service';
import {
  ActionEvent,
  ActionState,
  MessageDialogComponent,
  RadioOption
} from '../../common/message-dialog/message-dialog.component';
import { PUBLISHED_STATUS_VALUES } from '../../domain/nztcs/published-status';
import { Category } from '../../domain/nztcs/category';

interface PublishStatusOption {
  code: string;
  tooltip: string;
}

const PUBLISH_STATUS_TOOLTIPS: { [status: string]: string } = {
  CURRENT: 'A current assessment provides the latest conservation status of the species.',
  NOT_CURRENT: 'A non current assessment provides an earlier conservation status that has been reviewed'
};

@Component({
  selector: 'app-assessment-search',
  templateUrl: './assessment-search.component.html',
})
export class AssessmentSearchComponent implements OnInit {
  @ViewChild('messageDialog', { static: true })
  messageDialog: MessageDialogComponent;

  reportNames: string[];

  editStatusOptions = EDIT_STATUS_VALUES;
  publishedStatusOptions: PublishStatusOption[] = PUBLISHED_STATUS_VALUES.map((status) => ({
    code: status,
    tooltip: PUBLISH_STATUS_TOOLTIPS[status] || ''
  }));

  availableTags: Tag[];
  categoryList: Category[];
  umbrellaCategories = UmbrellaCategories.VALUES;
  taxonomicStatuses = TaxonomicStatus.VALUES;
  speciesTypes: string[];

  environmentOptions: string[];
  dataSource: Observable<any>;

  currentUser: User;

  exportRadioOptions: RadioOption[] = [
    { label: 'Yes, I want to get the current assessment of all species.', value: 1, checked: true },
    { label: 'No, I want to export the information as displayed in the original report.', value: 0, checked: false }
  ];

  constructor(
    public searchCtx: AssessmentSearchCtxService,
    private assessmentService: AssessmentService,
    private reportService: ReportService,
    private tagService: TagService,
    private authenticationService: AuthenticationService,
    private exportService: AssessmentExportService,
    private nztcsSpeciesService: NztcsSpeciesService,
    public nztcsSpeciesNameComparisonService: NztcsSpeciesNameComparisonService,
    private router: Router
  ) {
    this.assessmentService.categoryList().subscribe((categoryList) => {
      this.categoryList = categoryList;
    });

    this.dataSource = Observable.create((observer: any) => {
      observer.next(this.searchCtx.criteria.assessmentName);
    }).pipe(mergeMap((token: string) => this.getNameTokensAsObservable(token)));
  }

  getNameTokensAsObservable(token: string): Observable<any> {
    return this.assessmentService.findSpeciesNamesByNameCriteria(token);
  }

  ngOnInit() {
    this.speciesTypes = this.nztcsSpeciesService.getSpeciesTypes();
    this.environmentOptions = Environment.VALUES;
    this.authenticationService.currentUser$.subscribe(
      (currentUser) => (this.currentUser = currentUser)
    );
    this.refreshReportNames(this.searchCtx.criteria.showArchived, false);
    this.tagService
      .findAll()
      .subscribe((tagList) => (this.availableTags = tagList));
    this.searchAction();
  }

  searchAction() {
    this.searchCtx.search();
  }

  clearAction() {
    this.searchCtx.clear();
    this.searchAction();
  }

  onPageChanged(event: any): void {
    if (event.page && this.searchCtx.criteria) {
      this.searchCtx.criteria.pageNumber = event.page;
      this.searchAction();
    }
  }

  refreshReportNames(showArchived: boolean, performSearch: boolean) {
    this.reportService.getReportNames(showArchived).subscribe((reportNames) => {
      this.reportNames = reportNames;
      if (performSearch) {
        this.searchAction();
      }
    });
  }

  /**
   * See report search for more details about this...
   */
  onShowArchivedChanged() {
    this.refreshReportNames(!this.searchCtx.criteria.showArchived, true);
  }

  confirmExportAction() {

    this.assessmentService.findNotCurrentCount(this.searchCtx.criteria).subscribe(
      (count) => {
        console.log(`findNotCurrentCount: ${count}`);
        if(count > 0) {
          const messageText =
            'This report contains one or more assessments that are no longer current. Do you want to export the current assessment of all species?';
          this.messageDialog.start(messageText);
        }
        else {
          const bypassPopup: ActionEvent = new ActionEvent(ActionState.CONFIRMED, null);
          this.exportAction(bypassPopup);
        }
      }
    );
  }

  exportAction(actionEvent: ActionEvent) {
    if (actionEvent.state === ActionState.CONFIRMED) {
      if (actionEvent.selectedRadio !== null) {
        this.searchCtx.criteria.overrideExportWithCurrent = (actionEvent.selectedRadio === 1);
      }

      this.exportService.startExport(this.searchCtx.criteria).subscribe(
        () => {
          this.router.navigate(['/assessment-export']);
        },
        (errData) => {
          console.log(errData);
          this.router.navigate(['/assessment-export']);
        }
      );
    }
  }
}
